import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const CreateFolderModal = ({ 
  isOpen, 
  onClose, 
  onCreate, 
  type = 'folder', // 'folder' or 'base'
  parentFolder = null 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isPublic: false,
    inheritPermissions: true
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) {
      newErrors.name = `${type === 'base' ? 'Document base' : 'Folder'} name is required`;
    } else if (formData?.name?.length < 2) {
      newErrors.name = 'Name must be at least 2 characters long';
    } else if (formData?.name?.length > 50) {
      newErrors.name = 'Name must be less than 50 characters';
    }
    
    if (formData?.description && formData?.description?.length > 200) {
      newErrors.description = 'Description must be less than 200 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const newItem = {
        id: `${type}-${Date.now()}`,
        name: formData?.name?.trim(),
        description: formData?.description?.trim(),
        type: type,
        isPublic: type === 'base' ? formData?.isPublic : parentFolder?.isPublic || false,
        inheritPermissions: formData?.inheritPermissions,
        parentId: parentFolder?.id || null,
        createdAt: new Date()?.toISOString(),
        documentCount: 0,
        children: []
      };
      
      await onCreate(newItem);
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        isPublic: false,
        inheritPermissions: true
      });
      setErrors({});
      onClose();
    } catch (error) {
      setErrors({ submit: error?.message || 'Failed to create item' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Create New {type === 'base' ? 'Document Base' : 'Folder'}
            </h2>
            {parentFolder && (
              <p className="text-sm text-muted-foreground mt-1">
                in {parentFolder?.name}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            iconSize={20}
            onClick={onClose}
            disabled={loading}
          />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name Input */}
          <Input
            label={`${type === 'base' ? 'Document Base' : 'Folder'} Name`}
            type="text"
            placeholder={`Enter ${type === 'base' ? 'document base' : 'folder'} name`}
            value={formData?.name}
            onChange={(e) => handleInputChange('name', e?.target?.value)}
            error={errors?.name}
            required
            maxLength={50}
          />

          {/* Description Input */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description (Optional)
            </label>
            <textarea
              placeholder={`Describe the purpose of this ${type === 'base' ? 'document base' : 'folder'}`}
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              rows={3}
              maxLength={200}
            />
            {errors?.description && (
              <p className="text-sm text-error mt-1">{errors?.description}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {formData?.description?.length}/200 characters
            </p>
          </div>

          {/* Visibility Settings (Document Base Only) */}
          {type === 'base' && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Visibility Settings</h3>
              
              <Checkbox
                label="Make this document base public"
                description="Public document bases can be accessed by all users in the organization"
                checked={formData?.isPublic}
                onChange={(e) => handleInputChange('isPublic', e?.target?.checked)}
              />
            </div>
          )}

          {/* Permission Settings */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-foreground">Permission Settings</h3>
            
            <Checkbox
              label="Inherit permissions from parent"
              description={
                parentFolder 
                  ? `Inherit permissions from "${parentFolder?.name}"`
                  : "Use default organization permissions"
              }
              checked={formData?.inheritPermissions}
              onChange={(e) => handleInputChange('inheritPermissions', e?.target?.checked)}
            />
          </div>

          {/* Submit Error */}
          {errors?.submit && (
            <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <p className="text-sm text-error">{errors?.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Plus"
              iconPosition="left"
              loading={loading}
              disabled={loading}
            >
              Create {type === 'base' ? 'Document Base' : 'Folder'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateFolderModal;